import { useCallback, useEffect, useRef } from "react";

type RouteLoader = () => Promise<unknown>;

interface NetworkState {
  effectiveType?: string;
  saveData?: boolean;
}

const loadDashboard = () => import("@/features/dashboard/pages/DashboardPage");
const loadInvoices = () => import("@/features/invoices/pages/InvoicesUnifiedPage");
const loadPurchaseOrderBoard = () =>
  import("@/features/purchase-orders/board/pages/POManagementKanbanPage");
const loadTasks = () => import("@/features/tasks/pages/TasksPage");

/** Route chunks reachable directly from the persistent shell navigation. */
const SHELL_ROUTE_LOADERS = new Map<string, RouteLoader>([
  ["/", loadDashboard],
  ["/accounting", () => import("@/features/accounting/pages/AccountingPage")],
  ["/accounts", () => import("@/features/accounts/pages/AccountsPage")],
  ["/analytics", () => import("@/features/analytics/pages/AnalyticsDashboardPage")],
  ["/credits", () => import("@/features/credits/pages/CustomerCreditsPage")],
  ["/customer-payments", () => import("@/features/customer-payments/pages/CustomerPaymentsPage")],
  ["/estimates", () => import("@/features/estimates/pages/EstimatesUnifiedPage")],
  ["/expenses", () => import("@/features/expenses/pages/ExpenseDashboard")],
  ["/finance", () => import("@/features/finance/pages/FinanceDashboardPage")],
  ["/inventory", () => import("@/features/inventory/pages/InventoryPage")],
  [
    "/inventory-management",
    () => import("@/features/inventory/management/pages/InventoryManagementPage"),
  ],
  ["/invoices", loadInvoices],
  ["/invoices/create", loadInvoices],
  ["/notifications", () => import("@/features/notifications/pages/NotificationsPage")],
  ["/operations", () => import("@/features/operations/pages/OperationsPage")],
  ["/projects", () => import("@/features/projects/pages/ProjectsPage")],
  ["/purchase-orders", () => import("@/features/purchase-orders/pages/PurchaseOrdersUnifiedPage")],
  ["/purchase-orders/kanban", loadPurchaseOrderBoard],
  ["/settings", () => import("@/features/settings/pages/SettingsPage")],
  ["/sync", () => import("@/features/sync/pages/SyncDashboardPage")],
  ["/tasks", loadTasks],
  ["/tasks/activity", () => import("@/features/tasks/pages/TaskActivityPage")],
  ["/tasks/calendar", () => import("@/features/tasks/pages/TaskCalendarPage")],
  ["/tasks/search", () => import("@/features/tasks/pages/TaskSearchPage")],
  ["/theme", () => import("@/features/dev/pages/ThemeShowcasePage")],
  ["/vendor-credits", () => import("@/features/vendor-credits/pages/VendorCreditsPage")],
  ["/vendor-payments", () => import("@/features/vendor-payments/pages/VendorPaymentsPage")],
  ["/assistant", () => import("@/features/voice-agent/pages/VoiceAgentPage")],
]);

function currentNetworkState(): NetworkState | undefined {
  return (navigator as Navigator & { connection?: NetworkState }).connection;
}

function networkAllowsPrefetch(network: NetworkState | undefined): boolean {
  return (
    !network?.saveData && network?.effectiveType !== "slow-2g" && network?.effectiveType !== "2g"
  );
}

/**
 * Builds an intent prefetcher whose successful loads run once per chunk.
 *
 * Failures are swallowed and made retryable: route navigation still flows
 * through `lazyRoute`, so its stale-deploy recovery remains authoritative.
 */
export function createRoutePrefetcher(
  loaders: ReadonlyMap<string, RouteLoader>,
  getNetworkState: () => NetworkState | undefined = currentNetworkState,
) {
  const attempted = new Set<RouteLoader>();

  return (pathname: string) => {
    const loader = loaders.get(pathname);
    if (!loader) {
      return;
    }
    if (attempted.has(loader)) {
      return;
    }
    if (!networkAllowsPrefetch(getNetworkState())) {
      return;
    }

    attempted.add(loader);
    void loader().catch(() => {
      attempted.delete(loader);
    });
  };
}

const prefetchShellRoute = createRoutePrefetcher(SHELL_ROUTE_LOADERS);
const POINTER_INTENT_DELAY_MS = 90;

/** One delayed pointer-intent timer for the persistent navigation surface. */
export function useNavigationPrefetch() {
  const timerRef = useRef<number | undefined>(undefined);

  const cancelPrefetch = useCallback(() => {
    if (timerRef.current !== undefined) {
      window.clearTimeout(timerRef.current);
      timerRef.current = undefined;
    }
  }, []);

  const prefetchOnFocus = useCallback(
    (pathname: string) => {
      cancelPrefetch();
      prefetchShellRoute(pathname);
    },
    [cancelPrefetch],
  );

  const schedulePrefetch = useCallback(
    (pathname: string) => {
      cancelPrefetch();
      timerRef.current = window.setTimeout(() => {
        timerRef.current = undefined;
        prefetchShellRoute(pathname);
      }, POINTER_INTENT_DELAY_MS);
    },
    [cancelPrefetch],
  );

  useEffect(() => cancelPrefetch, [cancelPrefetch]);

  return { cancelPrefetch, prefetchOnFocus, schedulePrefetch } as const;
}
